<template>
  <BaseDialog
    width="560px"
    :show-text="false"
    text-align="left"
    footer-align="flex-end"
    :title="$t('carriers.import.schedule.Title')"
    :confirm-button-text="$t('buttons.save')"
    :cancel-button-text="$t('adminTitles.cancel')"
    :confirm-loading="saving"
    :confirm-disable="saving || !account || !valid"
    @confirm="save"
    @close="$emit('close')"
  >
    <div class="schedule">
      <p class="schedule__subtitle">{{ $t('carriers.import.schedule.Subtitle') }}</p>

      <!-- Several logins on one carrier: each reads on its own schedule. -->
      <el-select
        v-if="schedulable.length > 1"
        v-model="accountId"
        class="schedule__account"
        @change="load"
      >
        <el-option
          v-for="a in schedulable"
          :key="a.scrapeAccountId"
          :value="a.scrapeAccountId"
          :label="`${a.label || a.username} · ${a.portalName}`"
        />
      </el-select>

      <div class="schedule__switch">
        <span>{{ $t('carriers.import.schedule.ReadOnSchedule') }}</span>
        <el-switch v-model="form.enabled" />
      </div>

      <div class="schedule__body" :class="{ 'schedule__body--off': !form.enabled }">
        <p class="schedule__label">{{ $t('carriers.import.schedule.Frequency') }}</p>
        <el-radio-group v-model="form.frequency" size="medium" class="schedule__segmented">
          <el-radio-button v-for="f in frequencies" :key="f" :label="f">
            {{ $t(`carriers.import.schedule.frequency.${f}`) }}
          </el-radio-button>
        </el-radio-group>

        <template v-if="form.frequency === 'WEEKLY'">
          <p class="schedule__label">{{ $t('carriers.import.schedule.OnDays') }}</p>
          <el-checkbox-group v-model="form.weekdays" size="small" class="schedule__days">
            <el-checkbox-button v-for="d in 7" :key="d" :label="d">{{ weekdayName(d) }}</el-checkbox-button>
          </el-checkbox-group>
        </template>

        <template v-if="form.frequency === 'MONTHLY'">
          <p class="schedule__label">{{ $t('carriers.import.schedule.OnDay') }}</p>
          <el-input-number v-model="form.monthDay" :min="1" :max="31" size="small" />
          <p v-if="form.monthDay > 28" class="schedule__hint">{{ $t('carriers.import.schedule.MonthEnd') }}</p>
        </template>

        <p class="schedule__label">{{ $t('carriers.import.schedule.TimeOfDay') }}</p>
        <div class="schedule__time">
          <el-time-select
            v-model="form.time"
            :clearable="false"
            :picker-options="timeOptions"
            size="medium"
          />
          <span class="schedule__tz">{{ $t('carriers.import.schedule.MexicoCityTime') }}</span>
        </div>
        <p v-if="window" class="schedule__hint">
          {{ $t('carriers.import.schedule.NightWindow', { from: timeOptions.start, to: windowEnd }) }}
        </p>

        <template v-if="kindOptions.length > 1">
          <p class="schedule__label">{{ $t('carriers.import.schedule.WhatToRead') }}</p>
          <el-radio-group v-model="form.kinds">
            <el-radio v-for="k in kindOptions" :key="k" :label="k">{{ $t(`carriers.import.schedule.kinds.${k}`) }}</el-radio>
          </el-radio-group>
        </template>

        <!-- eslint-disable-next-line vue/no-v-html -- every piece is escaped in sentenceHtml -->
        <p class="schedule__sentence" v-html="sentenceHtml"></p>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import {
  apiError, minutesToTime, weekdayName,
} from '@/utils/carrierImport';

const escape = (s) => String(s).replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

/**
 * "Set a schedule" on a carrier row: when one of its logins is read with nobody
 * there. Saved on the login (scrape/account/schedule); the scheduled sync on the
 * backend reads it. Mexico City time, the only one the portals keep.
 */
export default {
  name: 'CarrierScheduleModal',
  components: { BaseDialog },
  props: {
    carrierName: { type: String, default: '' },
    accounts: { type: Array, default: () => [] },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      accountId: null,
      saving: false,
      frequencies: ['DAILY', 'WEEKLY', 'MONTHLY'],
      form: {
        enabled: true, frequency: 'DAILY', time: '06:30', weekdays: [1], monthDay: 1, kinds: 'BOTH',
      },
    };
  },
  computed: {
    ...mapGetters(['carrierImportPortals']),
    schedulable() {
      return this.accounts.filter((a) => this.kindsOf(a).length);
    },
    account() {
      return this.schedulable.find((a) => a.scrapeAccountId === this.accountId) || null;
    },
    portal() {
      return this.account && this.carrierImportPortals.find((p) => p.code === this.account.portal);
    },
    kindOptions() {
      const kinds = this.account ? this.kindsOf(this.account) : [];
      return kinds.length > 1 ? ['BOTH', ...kinds] : kinds;
    },
    window() {
      return (this.portal && this.portal.scheduleWindow) || null;
    },
    windowEnd() {
      return this.window ? minutesToTime(this.window.toMinute) : '';
    },
    /** Every 15 minutes, inside the portal's window when it has one. */
    timeOptions() {
      return {
        start: this.window ? minutesToTime(this.window.fromMinute) : '00:00',
        end: this.window ? minutesToTime(this.window.toMinute - (this.window.toMinute % 15)) : '23:45',
        step: '00:15',
      };
    },
    /** Switching off saves nothing but the switch: the fields need not make sense. */
    valid() {
      return !this.form.enabled || this.fieldsValid;
    },
    fieldsValid() {
      if (!this.form.time) return false;
      if (this.form.frequency === 'WEEKLY') return this.form.weekdays.length > 0;
      if (this.form.frequency === 'MONTHLY') return this.form.monthDay >= 1 && this.form.monthDay <= 31;
      return true;
    },
    /** "It will read payments at Quálitas every day at 06:30, Mexico City time." */
    sentenceHtml() {
      if (!this.form.enabled) return escape(this.$t('carriers.import.schedule.Off'));
      const b = (s) => `<b>${escape(s)}</b>`;
      let when;
      if (this.form.frequency === 'WEEKLY') {
        when = this.$t('carriers.import.schedule.when.WEEKLY', {
          days: [...this.form.weekdays].sort().map((d) => weekdayName(d, this.$i18n.locale, 'long')).join(', '),
        });
      } else if (this.form.frequency === 'MONTHLY') {
        when = this.$t('carriers.import.schedule.when.MONTHLY', { day: this.form.monthDay });
      } else {
        when = this.$t('carriers.import.schedule.when.DAILY');
      }
      return this.$t('carriers.import.schedule.Sentence', {
        what: b(this.$t(`carriers.import.schedule.reads.${this.form.kinds}`)),
        carrier: b(this.carrierName || this.$t('carriers.import.TheCarrier')),
        when: escape(when),
        time: b(this.form.time),
      });
    },
  },
  created() {
    this.accountId = (this.schedulable.find((a) => a.schedule && a.schedule.enabled) || this.schedulable[0] || {}).scrapeAccountId || null;
    this.load();
  },
  methods: {
    weekdayName(d) {
      return weekdayName(d, this.$i18n.locale);
    },
    kindsOf(account) {
      const portal = this.carrierImportPortals.find((p) => p.code === account.portal);
      return (portal && portal.schedulableKinds) || [];
    },
    /** The login's saved schedule, or a sensible first one: daily, at the start of what it allows. */
    load() {
      const a = this.account;
      if (!a) return;
      const s = a.schedule;
      const kinds = this.kindOptions;
      if (s) {
        this.form = {
          enabled: s.enabled,
          frequency: s.frequency,
          time: s.time,
          weekdays: s.weekdays && s.weekdays.length ? [...s.weekdays] : [1],
          monthDay: s.monthDay || 1,
          kinds: kinds.includes(s.kinds) ? s.kinds : kinds[0],
        };
      } else {
        this.form = {
          enabled: true,
          frequency: 'DAILY',
          time: this.window ? minutesToTime(this.window.fromMinute) : '06:30',
          weekdays: [1],
          monthDay: 1,
          kinds: kinds[0],
        };
      }
    },
    save() {
      if (!this.account || !this.valid) return;
      this.saving = true;
      // Switched off with fields left half-edited: keep what was saved (or a plain daily one),
      // so the endpoint, which checks the fields either way, takes the switch.
      const saved = this.account.schedule;
      const fields = this.fieldsValid ? this.form : {
        frequency: (saved && saved.frequency) || 'DAILY',
        time: (saved && saved.time) || this.timeOptions.start,
        weekdays: (saved && saved.weekdays) || [],
        monthDay: (saved && saved.monthDay) || null,
        kinds: (saved && saved.kinds) || this.form.kinds,
      };
      this.$store.dispatch('carrierImportSaveSchedule', {
        scrapeAccountId: this.account.scrapeAccountId,
        enabled: this.form.enabled,
        frequency: fields.frequency,
        time: fields.time,
        weekdays: fields.frequency === 'WEEKLY' ? fields.weekdays : null,
        monthDay: fields.frequency === 'MONTHLY' ? fields.monthDay : null,
        kinds: fields.kinds,
      })
        .then((item) => {
          this.$notify.success({ message: this.$t(this.form.enabled ? 'carriers.import.schedule.Saved' : 'carriers.import.schedule.TurnedOff') });
          this.$emit('saved', item);
          this.$emit('close');
        })
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule {
  text-align: left;
  margin-bottom: 8px;

  &__subtitle {
    font-size: 14px;
    color: var(--solid-dark-purple);
    margin: -6px 0 16px;
  }

  &__account { width: 100%; margin-bottom: 14px; }

  &__switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0 14px;
    border-bottom: 1px solid var(--solid-lavender-grey);
    font-size: 14px;
    color: var(--solid-black-purple);
  }

  &__body {
    transition: opacity 0.15s;

    &--off { opacity: 0.45; pointer-events: none; }
  }

  &__label {
    font-size: 13px;
    color: var(--solid-dark-purple);
    margin: 16px 0 8px;
  }

  &__segmented ::v-deep .el-radio-button__inner { min-width: 96px; }

  &__days ::v-deep .el-checkbox-button__inner { min-width: 52px; }

  &__time {
    display: flex;
    align-items: center;
    gap: 12px;

    ::v-deep .el-date-editor { width: 130px; }
  }

  &__tz { font-size: 13px; color: var(--solid-dark-purple); }

  &__hint {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 6px;
  }

  &__sentence {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid var(--solid-lavender-grey);
    font-size: 14px;
    line-height: 1.5;
    color: var(--solid-dark-purple);

    ::v-deep b { color: var(--solid-black-purple); }
  }
}
</style>
